import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as XLSX from 'xlsx';
import {
  Users,
  ArrowLeft,
  Package,
  TrendingUp,
  TruckIcon,
  Download,
  RefreshCw,
  FileText,
  Camera,
  MessageSquare,
  Image,
  Loader2,
  ChevronRight,
  ChevronDown,
  Clock,
  User,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import FilterDrawer from '@/components/FilterDrawer';
import {
  fetchInventarioClientesByCliente,
  deleteInventarioCliente,
  recalcularInventarioCliente,
} from '@/services/inventarioClientes.service';
import { fetchEntregasClientes } from '@/services/entregasClientes.service';
import { getAlistamientosByLavanderia } from '@/pages/laundry/alistamientos/services/alistamientos.services';
import { fetchMovimientosClientes } from '@/services/movimientosClientes.service';
import { getDownloadUrlOrOpen } from '@/services/documentsService';
import InformePanel from './InformePanel';
import PqrsfPanel from './PqrsfPanel';
import TicketModal from './TicketModal';
import TabConFiltros from './TabConFiltros';
import {
  getColumnsInventario,
  columnsMovimientos,
  getColumnsEntregas,
  getColumnsRecolecciones,
  columnsAlistamientos,
} from './columns';

const ITEMS_PER_PAGE = 10;

const fmtDT = (d) => {
  if (!d) return '-';
  return new Date(d).toLocaleString('es-CO', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};


const EvidenciasPanel = ({ clienteId }) => {
  const [recolecciones, setRecolecciones] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [evidenciaUrls, setEvidenciaUrls] = useState({});
  const [loadingEvidencia, setLoadingEvidencia] = useState({});

  const loadData = useCallback(async () => {
    if (!clienteId) return;
    setIsLoading(true);
    try {
      const res = await fetchEntregasClientes({
        query: { 'cliente.id': clienteId, tipo: 'Recoleccion', 'evidencias.0': { $exists: true } },
        limit: 200,
        page: 1,
        sort: { fecha_creacion: -1 },
      });
      setRecolecciones(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [clienteId]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const loadEvidencia = useCallback(async (tx) => {
    if (!Array.isArray(tx.evidencias) || tx.evidencias.length === 0) return;
    if (evidenciaUrls[tx._id] !== undefined || loadingEvidencia[tx._id]) return;
    setLoadingEvidencia((prev) => ({ ...prev, [tx._id]: true }));
    try {
      const urls = await Promise.all(
        tx.evidencias.map((evId) =>
          getDownloadUrlOrOpen({ id: evId, openInNewTab: false }).catch(() => null),
        ),
      );
      setEvidenciaUrls((prev) => ({ ...prev, [tx._id]: urls.filter(Boolean) }));
    } catch {
      setEvidenciaUrls((prev) => ({ ...prev, [tx._id]: [] }));
    } finally {
      setLoadingEvidencia((prev) => ({ ...prev, [tx._id]: false }));
    }
  }, [evidenciaUrls, loadingEvidencia]);

  useEffect(() => {
    recolecciones.forEach((tx) => {
      if (expanded.has(tx._id)) loadEvidencia(tx);
    });
  }, [expanded]); // eslint-disable-line

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Camera className="h-5 w-5 text-purple-600" />
              Evidencias Fotográficas
            </CardTitle>
            <CardDescription className="mt-0.5">
              Registro fotográfico de prendas manchadas o desgastadas en recolecciones
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={loadData} disabled={isLoading} className="shrink-0">
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto" />
            <p className="text-sm text-gray-400 mt-2">Cargando evidencias...</p>
          </div>
        ) : recolecciones.length === 0 ? (
          <div className="text-center py-12">
            <Image className="h-12 w-12 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-500 font-medium">Sin evidencias fotográficas registradas</p>
            <p className="text-xs text-gray-400 mt-1">Las fotos de prendas manchadas o desgastadas aparecerán aquí</p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-gray-400 mb-3">{recolecciones.length} recolección{recolecciones.length !== 1 ? 'es' : ''} con evidencias</p>
            {recolecciones.map((tx) => {
              const isExpanded = expanded.has(tx._id);
              const items = tx.items || [];
              const totalItems = items.reduce((s, i) => s + (i.cantidad_alistada || 0), 0);
              return (
                <div key={tx._id} className="border rounded-xl overflow-hidden">
                  <button
                    onClick={() => toggle(tx._id)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="p-2 bg-purple-100 rounded-lg shrink-0">
                      <Camera className="h-4 w-4 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-gray-800">{tx.consecutivo}</span>
                        <Badge className="bg-purple-100 text-purple-700 text-xs border-0">
                          {tx.evidencias.length} foto{tx.evidencias.length !== 1 ? 's' : ''}
                        </Badge>
                        <span className="text-xs text-gray-400">{totalItems} prendas</span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {fmtDT(tx.fecha_recoleccion || tx.fecha_creacion)}
                        </span>
                        {tx.conductor?.nombre && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {tx.conductor.nombre}
                          </span>
                        )}
                      </div>
                    </div>
                    {isExpanded
                      ? <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                      : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />}
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-5 pt-3 bg-gray-50 border-t space-y-4">
                      {loadingEvidencia[tx._id] ? (
                        <div className="flex items-center gap-2 py-2 text-sm text-gray-400">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Cargando fotos...
                        </div>
                      ) : (
                        <>
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                              Evidencias fotográficas
                            </p>
                            <div className="flex gap-3 flex-wrap">
                              {(evidenciaUrls[tx._id] || []).map((url, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => window.open(url, '_blank')}
                                  className="rounded-xl overflow-hidden border-2 border-white hover:border-purple-400 hover:shadow-lg transition-all"
                                >
                                  <img src={url} alt={`Evidencia ${idx + 1}`} className="w-28 h-28 object-cover" />
                                </button>
                              ))}
                              {evidenciaUrls[tx._id]?.length === 0 && (
                                <p className="text-sm text-gray-400 py-2">No se pudieron cargar las fotos</p>
                              )}
                            </div>
                          </div>
                          {items.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                Prendas recolectadas
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {items.map((item, idx) => (
                                  <span key={idx} className="bg-white border border-gray-200 rounded-full px-3 py-1 text-xs text-gray-700">
                                    {item.nombre} <strong className="text-gray-900">({item.cantidad_alistada || 0})</strong>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ClienteInventarioDetail = () => {
  const { idLavanderia, clienteId } = useParams();
  const location = useLocation();
  const { toast } = useToast();

  const [cliente, setCliente] = useState(location.state?.cliente || null);
  const [activeTab, setActiveTab] = useState('inventario');
  const [searchTerm, setSearchTerm] = useState('');

  // Estados para filtros
  const [filters, setFilters] = useState({});
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Total de prendas en inventario
  const [totalPrendas, setTotalPrendas] = useState(0);

  // Estados para Inventario
  const [inventario, setInventario] = useState([]);
  const [isLoadingInventario, setIsLoadingInventario] = useState(false);
  const [currentPageInventario, setCurrentPageInventario] = useState(1);
  const [totalInventario, setTotalInventario] = useState(0);

  // Estados para Movimientos
  const [movimientos, setMovimientos] = useState([]);
  const [isLoadingMovimientos, setIsLoadingMovimientos] = useState(false);
  const [currentPageMovimientos, setCurrentPageMovimientos] = useState(1);
  const [totalMovimientos, setTotalMovimientos] = useState(0);

  // Estados para Entregas
  const [entregas, setEntregas] = useState([]);
  const [isLoadingEntregas, setIsLoadingEntregas] = useState(false);
  const [currentPageEntregas, setCurrentPageEntregas] = useState(1);
  const [totalEntregas, setTotalEntregas] = useState(0);

  // Estados para Recolecciones
  const [recolecciones, setRecolecciones] = useState([]);
  const [isLoadingRecolecciones, setIsLoadingRecolecciones] = useState(false);
  const [currentPageRecolecciones, setCurrentPageRecolecciones] = useState(1);
  const [totalRecolecciones, setTotalRecolecciones] = useState(0);

  // Estados para Alistamientos
  const [alistamientos, setAlistamientos] = useState([]);
  const [isLoadingAlistamientos, setIsLoadingAlistamientos] = useState(false);
  const [currentPageAlistamientos, setCurrentPageAlistamientos] = useState(1);
  const [totalAlistamientos, setTotalAlistamientos] = useState(0);

  // Ticket modal
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Recalcular inventario
  const [isRecalculating, setIsRecalculating] = useState(false);

  useEffect(() => {
    if (activeTab === 'inventario') {
      loadInventario();
    } else if (activeTab === 'movimientos') {
      loadMovimientos();
    } else if (activeTab === 'entregas') {
      loadEntregas();
    } else if (activeTab === 'recolecciones') {
      loadRecolecciones();
    } else if (activeTab === 'alistamientos') {
      loadAlistamientos();
    }
  }, [activeTab, currentPageInventario, currentPageMovimientos, currentPageEntregas, currentPageRecolecciones, currentPageAlistamientos, clienteId, searchTerm, filters]);

  const loadInventario = async () => {
    setIsLoadingInventario(true);
    try {
      const response = await fetchInventarioClientesByCliente(clienteId);
      const inventarioData = Array.isArray(response.data) ? response.data : [];
      setInventario(inventarioData);
      setTotalInventario(inventarioData.length);

      // Calcular total de prendas
      const total = inventarioData.reduce((sum, item) => sum + (item.cantidad || 0), 0);
      setTotalPrendas(total);
    } catch (error) {
      console.error('Error al cargar inventario:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el inventario del cliente',
        variant: 'destructive',
      });
      setInventario([]);
    } finally {
      setIsLoadingInventario(false);
    }
  };

  const loadMovimientos = async () => {
    setIsLoadingMovimientos(true);
    try {
      const query = { clienteId };
      if (filters.fechaInicio) query.fecha = { ...query.fecha, $gte: new Date(filters.fechaInicio + 'T00:00:00').toISOString() };
      if (filters.fechaFin) query.fecha = { ...query.fecha, $lte: new Date(filters.fechaFin + 'T23:59:59').toISOString() };
      const response = await fetchMovimientosClientes({ query, limit: ITEMS_PER_PAGE, page: currentPageMovimientos });
      setMovimientos(response.data || []);
      setTotalMovimientos(response.totalRecords || 0);
    } catch (error) {
      console.error('Error al cargar movimientos:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los movimientos',
        variant: 'destructive',
      });
      setMovimientos([]);
      setTotalMovimientos(0);
    } finally {
      setIsLoadingMovimientos(false);
    }
  };

  const loadEntregas = async () => {
    setIsLoadingEntregas(true);
    try {
      const query = { 'cliente.id': clienteId, tipo: 'Entrega' };
      if (filters.fechaInicio) query.fecha_creacion = { ...query.fecha_creacion, $gte: new Date(filters.fechaInicio + 'T00:00:00').toISOString() };
      if (filters.fechaFin) query.fecha_creacion = { ...query.fecha_creacion, $lte: new Date(filters.fechaFin + 'T23:59:59').toISOString() };
      const response = await fetchEntregasClientes({ query, limit: ITEMS_PER_PAGE, page: currentPageEntregas });
      setEntregas(response.data || []);
      setTotalEntregas(response.totalRecords || 0);
    } catch (error) {
      console.error('Error al cargar entregas:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las entregas',
        variant: 'destructive',
      });
      setEntregas([]);
      setTotalEntregas(0);
    } finally {
      setIsLoadingEntregas(false);
    }
  };

  const loadRecolecciones = async () => {
    setIsLoadingRecolecciones(true);
    try {
      const query = { 'cliente.id': clienteId, tipo: 'Recoleccion' };
      if (filters.fechaInicio) query.fecha_creacion = { ...query.fecha_creacion, $gte: new Date(filters.fechaInicio + 'T00:00:00').toISOString() };
      if (filters.fechaFin) query.fecha_creacion = { ...query.fecha_creacion, $lte: new Date(filters.fechaFin + 'T23:59:59').toISOString() };
      const response = await fetchEntregasClientes({ query, limit: ITEMS_PER_PAGE, page: currentPageRecolecciones });
      setRecolecciones(response.data || []);
      setTotalRecolecciones(response.totalRecords || 0);
    } catch (error) {
      console.error('Error al cargar recolecciones:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las recolecciones',
        variant: 'destructive',
      });
      setRecolecciones([]);
      setTotalRecolecciones(0);
    } finally {
      setIsLoadingRecolecciones(false);
    }
  };

  const loadAlistamientos = async () => {
    setIsLoadingAlistamientos(true);
    try {
      const response = await getAlistamientosByLavanderia(idLavanderia, {
        query: {
          'cliente.id': clienteId,
          estado: { $in: ['Alistado', 'Pendiente'] },
        },
        page: currentPageAlistamientos,
        limit: ITEMS_PER_PAGE,
      });
      setAlistamientos(response.data || []);
      setTotalAlistamientos(response.totalRecords || 0);
    } catch (error) {
      console.error('Error al cargar alistamientos:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los alistamientos',
        variant: 'destructive',
      });
      setAlistamientos([]);
      setTotalAlistamientos(0);
    } finally {
      setIsLoadingAlistamientos(false);
    }
  };

  // Manejar aplicación de filtros
  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setIsFilterDrawerOpen(false);
  };

  // Manejar eliminación de filtro individual
  const handleRemoveFilter = (filterId) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters[filterId];
      return newFilters;
    });
  };

  // Campos de filtro para el drawer
  const filterFields = [
    {
      id: 'fechaInicio',
      label: 'Fecha Inicio',
      type: 'date',
      placeholder: 'Fecha desde'
    },
    {
      id: 'fechaFin',
      label: 'Fecha Fin',
      type: 'date',
      placeholder: 'Fecha hasta'
    }
  ];

  const handleRecalcular = async () => {
    if (!confirm(
      '¿Está seguro de recalcular el inventario?\n\n' +
      'Esta acción borrará todos los registros de inventario y movimientos del cliente ' +
      'y los regenerará desde las entregas y recolecciones completadas.\n\n' +
      'Continuará?'
    )) return;

    setIsRecalculating(true);
    try {
      const result = await recalcularInventarioCliente(clienteId);
      const d = result.data || {};
      toast({
        title: 'Inventario recalculado',
        description: `${d.inventarioRecords ?? 0} tipos de prenda · ${d.movimientosRecords ?? 0} movimientos · ${d.recoleccionesProcessed ?? 0} recolecciones · ${d.entregasProcessed ?? 0} entregas procesadas.`,
      });
      loadInventario();
      if (activeTab === 'movimientos') loadMovimientos();
    } catch (error) {
      console.error('Error al recalcular:', error);
      toast({
        title: 'Error',
        description: 'No se pudo recalcular el inventario',
        variant: 'destructive',
      });
    } finally {
      setIsRecalculating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Está seguro de eliminar este elemento del inventario?')) {
      return;
    }

    try {
      await deleteInventarioCliente(id);
      toast({
        title: 'Éxito',
        description: 'Elemento eliminado correctamente',
      });
      loadInventario();
    } catch (error) {
      console.error('Error al eliminar:', error);
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el elemento',
        variant: 'destructive',
      });
    }
  };

  // ─── Export functions ────────────────────────────────────────

  const exportInventario = async () => {
    try {
      const response = await fetchInventarioClientesByCliente(clienteId);
      const data = Array.isArray(response.data) ? response.data : [];
      const rows = data.map(r => ({
        'Tipo de Prenda': r.tipoPrenda?.nombre || 'N/A',
        'Cantidad': r.cantidad || 0,
        'Peso (kg)': r.peso || '',
        'Descripción': r.descripcion || '',
        'Estado': r.estado || '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Inventario');
      XLSX.writeFile(wb, `inventario_${clienteId}.xlsx`);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo exportar', variant: 'destructive' });
    }
  };

  const exportMovimientos = async () => {
    try {
      const query = { clienteId };
      if (filters.fechaInicio) query.fecha = { ...query.fecha, $gte: new Date(filters.fechaInicio + 'T00:00:00').toISOString() };
      if (filters.fechaFin) query.fecha = { ...query.fecha, $lte: new Date(filters.fechaFin + 'T23:59:59').toISOString() };
      const response = await fetchMovimientosClientes({ query, limit: 10000, page: 1 });
      const data = response.data || [];
      const rows = data.map(r => ({
        'Fecha': new Date(r.fecha).toLocaleString('es-ES'),
        'Tipo': r.tipo || '',
        'Prenda': r.tipoPrenda?.nombre || '',
        'Cantidad': r.cantidad || 0,
        'Referencia': r.referenciaConsecutivo || '',
        'Observaciones': r.observaciones || '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Movimientos');
      XLSX.writeFile(wb, `movimientos_${clienteId}.xlsx`);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo exportar', variant: 'destructive' });
    }
  };

  const exportEntregas = async () => {
    try {
      const query = { 'cliente.id': clienteId, tipo: 'Entrega' };
      if (filters.fechaInicio) query.fecha_creacion = { ...query.fecha_creacion, $gte: new Date(filters.fechaInicio + 'T00:00:00').toISOString() };
      if (filters.fechaFin) query.fecha_creacion = { ...query.fecha_creacion, $lte: new Date(filters.fechaFin + 'T23:59:59').toISOString() };
      const response = await fetchEntregasClientes({ query, limit: 10000, page: 1 });
      const data = response.data || [];
      const rows = data.map(r => ({
        'Consecutivo': r.consecutivo || '',
        'Fecha': new Date(r.fecha_entrega || r.fecha_creacion).toLocaleDateString('es-ES'),
        'Estado': r.estado || '',
        'Prendas': r.items?.reduce((s, i) => s + (i.cantidad_confirmada || i.cantidad_alistada || 0), 0) || 0,
        'Conductor': r.conductor?.nombre || '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Entregas');
      XLSX.writeFile(wb, `entregas_${clienteId}.xlsx`);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo exportar', variant: 'destructive' });
    }
  };

  const exportRecolecciones = async () => {
    try {
      const query = { 'cliente.id': clienteId, tipo: 'Recoleccion' };
      if (filters.fechaInicio) query.fecha_creacion = { ...query.fecha_creacion, $gte: new Date(filters.fechaInicio + 'T00:00:00').toISOString() };
      if (filters.fechaFin) query.fecha_creacion = { ...query.fecha_creacion, $lte: new Date(filters.fechaFin + 'T23:59:59').toISOString() };
      const response = await fetchEntregasClientes({ query, limit: 10000, page: 1 });
      const data = response.data || [];
      const rows = data.map(r => ({
        'Consecutivo': r.consecutivo || '',
        'Fecha': new Date(r.fecha_recoleccion || r.fecha_creacion).toLocaleDateString('es-ES'),
        'Estado': r.estado || '',
        'Prendas': r.items?.reduce((s, i) => s + (i.cantidad_confirmada || i.cantidad_alistada || 0), 0) || 0,
        'Conductor': r.conductor?.nombre || '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Recolecciones');
      XLSX.writeFile(wb, `recolecciones_${clienteId}.xlsx`);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo exportar', variant: 'destructive' });
    }
  };


  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-2">
      <div className="mb-4">
        <Button asChild variant="outline">
          <Link to={`/centros-lavado/${idLavanderia}/inventario-clientes`}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver a Inventario por Cliente
          </Link>
        </Button>
      </div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Users className="h-8 w-8 text-blue-600" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-2xl font-bold text-foreground">
                    {cliente?.nombre || 'Cliente'}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Inventario de prendas del cliente
                  </CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>
      </motion.div>

      {/* Sidebar + Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card>
          <CardContent className="p-0">
            <div className="flex min-h-[600px]">

              {/* ── Sidebar ── */}
              <div className="w-52 shrink-0 border-r bg-gray-50 rounded-l-lg flex flex-col gap-0.5 p-2">
                {[
                  { id: 'informe-cliente',  label: 'Informe cliente',  icon: FileText,      badge: null, active: 'bg-teal-50 text-teal-700',   badge_c: '' },
                  { id: 'inventario',     label: 'Inventario en lavandería',      icon: Package,    badge: totalInventario,    active: 'bg-purple-50 text-purple-700', icon_c: 'text-purple-500', badge_c: 'bg-purple-100 text-purple-700' },
                  { id: 'recolecciones',  label: 'Recolecciones',   icon: TruckIcon,  badge: totalRecolecciones,  active: 'bg-orange-50 text-orange-700',icon_c: 'text-orange-500', badge_c: 'bg-orange-100 text-orange-700'},
                  { id: 'entregas',       label: 'Entregas',        icon: TruckIcon,  badge: totalEntregas,       active: 'bg-green-50 text-green-700',  icon_c: 'text-green-500',  badge_c: 'bg-green-100 text-green-700' },
                  { id: 'movimientos',    label: 'Movimientos',     icon: TrendingUp, badge: totalMovimientos,    active: 'bg-blue-50 text-blue-700',    icon_c: 'text-blue-500',   badge_c: 'bg-blue-100 text-blue-700'   },
                  { id: 'alistamientos',  label: 'Alistamientos',   icon: Package,    badge: totalAlistamientos,  active: 'bg-indigo-50 text-indigo-700',icon_c: 'text-indigo-500', badge_c: 'bg-indigo-100 text-indigo-700'},
                  { id: 'evidencias',       label: 'Evidencias',       icon: Camera,        badge: null, active: 'bg-purple-50 text-purple-700', badge_c: '' },
                  { id: 'pqrsf',            label: 'PQRSF',            icon: MessageSquare, badge: null, active: 'bg-amber-50 text-amber-700',  badge_c: '' },
                ].map(({ id, label, icon: Icon, badge, active, badge_c, soon }) => {
                  const isActive = activeTab === id;
                  return (
                    <button
                      key={id}
                      onClick={() => setActiveTab(id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-left ${
                        isActive ? `${active} shadow-sm` : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
                      }`}
                    >
                      <Icon className={`h-4 w-4 shrink-0 ${isActive ? '' : 'text-gray-400'}`} />
                      <span className="flex-1 leading-tight">{label}</span>
                      {soon && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 bg-amber-100 text-amber-600">
                          Pronto
                        </span>
                      )}
                      {!soon && badge > 0 && (
                        <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${
                          isActive ? badge_c : 'bg-gray-200 text-gray-500'
                        }`}>
                          {badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* ── Content ── */}
              <div className="flex-1 min-w-0 p-5">

                {/* Inventario */}
                {activeTab === 'inventario' && (
                  <div className="space-y-4">
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <Package className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Total de Prendas en Inventario</p>
                            <p className="text-2xl font-bold text-purple-700">{totalPrendas.toLocaleString()}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" onClick={exportInventario} className="flex items-center gap-2">
                            <Download className="h-4 w-4" />
                            Exportar
                          </Button>
                          <Button
                            variant="outline"
                            onClick={handleRecalcular}
                            disabled={isRecalculating}
                            className="border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-900"
                            title="Recalcula el inventario y los movimientos desde las entregas y recolecciones registradas"
                          >
                            <RefreshCw className={`h-4 w-4 mr-2 ${isRecalculating ? 'animate-spin' : ''}`} />
                            {isRecalculating ? 'Recalculando...' : 'Recalcular inventario'}
                          </Button>
                        </div>
                      </div>
                    </div>
                    <DataTable
                      columns={getColumnsInventario(handleDelete)}
                      data={inventario}
                      isLoading={isLoadingInventario}
                      showDefaultActions={false}
                    />
                  </div>
                )}

                {/* Movimientos */}
                {activeTab === 'movimientos' && (
                  <TabConFiltros
                    filters={filters}
                    filterFields={filterFields}
                    onRemoveFilter={handleRemoveFilter}
                    onOpenFilterDrawer={() => setIsFilterDrawerOpen(true)}
                    onExport={exportMovimientos}
                    columns={columnsMovimientos}
                    data={movimientos}
                    isLoading={isLoadingMovimientos}
                    hideActionsColumn
                    currentPage={currentPageMovimientos}
                    totalItems={totalMovimientos}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPageMovimientos}
                  />
                )}

                {/* Recolecciones */}
                {activeTab === 'recolecciones' && (
                  <TabConFiltros
                    filters={filters}
                    filterFields={filterFields}
                    onRemoveFilter={handleRemoveFilter}
                    onOpenFilterDrawer={() => setIsFilterDrawerOpen(true)}
                    onExport={exportRecolecciones}
                    columns={getColumnsRecolecciones(setSelectedTicket)}
                    data={recolecciones}
                    isLoading={isLoadingRecolecciones}
                    hideActionsColumn
                    currentPage={currentPageRecolecciones}
                    totalItems={totalRecolecciones}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPageRecolecciones}
                  />
                )}

                {/* Entregas */}
                {activeTab === 'entregas' && (
                  <TabConFiltros
                    filters={filters}
                    filterFields={filterFields}
                    onRemoveFilter={handleRemoveFilter}
                    onOpenFilterDrawer={() => setIsFilterDrawerOpen(true)}
                    onExport={exportEntregas}
                    columns={getColumnsEntregas(setSelectedTicket)}
                    data={entregas}
                    isLoading={isLoadingEntregas}
                    hideActionsColumn
                    currentPage={currentPageEntregas}
                    totalItems={totalEntregas}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPageEntregas}
                  />
                )}

                {/* Alistamientos */}
                {activeTab === 'alistamientos' && (
                  <TabConFiltros
                    filters={filters}
                    filterFields={filterFields}
                    onRemoveFilter={handleRemoveFilter}
                    onOpenFilterDrawer={() => setIsFilterDrawerOpen(true)}
                    columns={columnsAlistamientos}
                    data={alistamientos}
                    isLoading={isLoadingAlistamientos}
                    showDefaultActions={false}
                    currentPage={currentPageAlistamientos}
                    totalItems={totalAlistamientos}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPageAlistamientos}
                  />
                )}

                {/* Informe cliente */}
                {activeTab === 'informe-cliente' && (
                  <InformePanel clienteId={clienteId} clienteNombre={cliente?.nombre} />
                )}

                {/* Evidencias fotográficas */}
                {activeTab === 'evidencias' && (
                  <EvidenciasPanel clienteId={clienteId} />
                )}

                {/* PQRSF */}
                {activeTab === 'pqrsf' && (
                  <PqrsfPanel
                    clienteId={clienteId}
                    clienteNombre={cliente?.nombre}
                    clienteNit={cliente?.nit}
                    idLavanderia={idLavanderia}
                  />
                )}

              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        onApply={handleApplyFilters}
        fields={filterFields}
        initialValues={filters}
      />

      {/* Ticket Modal */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="max-w-lg p-0 gap-0">
          <DialogTitle className="sr-only">Ticket</DialogTitle>
          {selectedTicket && (
            <TicketModal record={selectedTicket} onClose={() => setSelectedTicket(null)} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClienteInventarioDetail;
